import * as React from "react";
import {
    Button,
    Checkbox,
    Input,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Toast,
    ToastTitle,
    Toaster,
    useId,
    useToastController,
} from "@fluentui/react-components";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@fluentui/react-table";
import { Add24Regular, CheckmarkCircle24Regular, Delete24Regular, Dismiss24Regular, Edit24Regular, Info24Regular } from "@fluentui/react-icons";
import { apiUrl } from "../../utils/api";
import { Bank } from "./types";

type BankManagementSectionProps = {
    contactId?: number;
    banks: Bank[];
    title?: string;
    onBanksChange?: (banks: Bank[]) => void;
};

export const BankManagementSection: React.FC<BankManagementSectionProps> = ({
    contactId,
    banks,
    title = "Bankverbindungen",
    onBanksChange,
}) => {
    const [bankList, setBankList] = React.useState<Bank[]>(banks);
    const [selectedBank, setSelectedBank] = React.useState<Bank | null>(null);

    const [bankDesc, setBankDesc] = React.useState("");
    const [bankName, setBankName] = React.useState("");
    const [bankIban, setBankIban] = React.useState("");
    const [bankBic, setBankBic] = React.useState("");
    const [bankDefault, setBankDefault] = React.useState(false);
    const [bankAccountHolder, setBankAccountHolder] = React.useState("");
    const [bankDirectDebit, setBankDirectDebit] = React.useState(false);

    const toasterId = useId("bank-toaster");
    const { dispatchToast } = useToastController(toasterId);

    React.useEffect(() => {
        setBankList(banks);
    }, [banks]);

    const setBankListAndNotify = React.useCallback(
        (updater: Bank[] | ((prev: Bank[]) => Bank[])) => {
            setBankList((prev) => {
                const next = typeof updater === "function" ? (updater as (prev: Bank[]) => Bank[])(prev) : updater;
                onBanksChange?.(next);
                return next;
            });
        },
        [onBanksChange],
    );

    React.useEffect(() => {
        if (selectedBank) {
            setBankDesc(selectedBank.bankDescription || "");
            setBankName(selectedBank.bankName || "");
            setBankIban(selectedBank.iban || "");
            setBankBic(selectedBank.bic || "");
            setBankDefault(selectedBank.isDefault);
            setBankAccountHolder(selectedBank.accountHolder || "");
            setBankDirectDebit(selectedBank.directDebit || false);
            return;
        }

        setBankDesc("");
        setBankName("");
        setBankIban("");
        setBankBic("");
        setBankDefault(false);
        setBankAccountHolder("");
        setBankDirectDebit(false);
    }, [selectedBank]);

    const handleNewBank = () => {
        setSelectedBank({
            id: Date.now(),
            bankDescription: "",
            iban: "",
            bic: "",
            bankName: "",
            isDefault: false,
            accountHolder: "",
            directDebit: false,
        });
    };

    const handleSaveBank = async () => {
        if (!selectedBank) {
            return;
        }

        const payload = {
            bankDescription: bankDesc,
            bankName,
            iban: bankIban,
            bic: bankBic,
            isDefault: bankDefault,
            accountHolder: bankAccountHolder,
            directDebit: bankDirectDebit,
        };

        if (!contactId) {
            const isNew = !bankList.some((bank) => bank.id === selectedBank.id);
            const nextBank = { ...selectedBank, ...payload };
            setBankListAndNotify((prev) =>
                isNew ? [...prev, nextBank] : prev.map((bank) => (bank.id === selectedBank.id ? nextBank : bank)),
            );
            setSelectedBank(null);
            dispatchToast(<Toast><ToastTitle>Bankverbindung gespeichert</ToastTitle></Toast>, { intent: "success" });
            return;
        }

        try {
            const isNew = !bankList.some((bank) => bank.id === selectedBank.id);

            if (isNew) {
                const response = await fetch(apiUrl(`contact/${contactId}/bank`), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const newBank = await response.json();
                setBankListAndNotify((prev) => [...prev, newBank]);
            } else {
                const response = await fetch(apiUrl(`contact/${contactId}/bank/${selectedBank.id}`), {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const updatedBank = await response.json();
                setBankListAndNotify((prev) => prev.map((bank) => (bank.id === selectedBank.id ? updatedBank : bank)));
            }

            setSelectedBank(null);
            dispatchToast(<Toast><ToastTitle>Bankverbindung gespeichert</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error saving bank:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Speichern</ToastTitle></Toast>, { intent: "error" });
        }
    };

    const handleDeleteBank = async (id: number) => {
        if (!contactId) {
            setBankListAndNotify((prev) => prev.filter((bank) => bank.id !== id));
            if (selectedBank?.id === id) {
                setSelectedBank(null);
            }
            dispatchToast(<Toast><ToastTitle>Bankverbindung gelöscht</ToastTitle></Toast>, { intent: "success" });
            return;
        }

        try {
            await fetch(apiUrl(`contact/${contactId}/bank/${id}`), { method: "DELETE" });
            setBankListAndNotify((prev) => prev.filter((bank) => bank.id !== id));
            if (selectedBank?.id === id) {
                setSelectedBank(null);
            }
            dispatchToast(<Toast><ToastTitle>Bankverbindung gelöscht</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error deleting bank:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Löschen</ToastTitle></Toast>, { intent: "error" });
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Toaster toasterId={toasterId} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <h3>{title}</h3>
                <Button icon={<Add24Regular />} onClick={handleNewBank}>Neu</Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>Bezeichnung</TableHeaderCell>
                        <TableHeaderCell>Kontoinhaber</TableHeaderCell>
                        <TableHeaderCell>Bank</TableHeaderCell>
                        <TableHeaderCell>IBAN</TableHeaderCell>
                        <TableHeaderCell style={{ width: "100px" }}>BIC</TableHeaderCell>
                        <TableHeaderCell style={{ width: "80px" }}>Einzug</TableHeaderCell>
                        <TableHeaderCell style={{ width: "100px" }}>Aktion</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[...bankList]
                        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                        .map((bank) => (
                            <TableRow
                                key={bank.id}
                                onClick={() => setSelectedBank(bank)}
                                style={{ cursor: "pointer", background: selectedBank?.id === bank.id ? "#f0f6ff" : undefined }}
                            >
                                <TableCell>{bank.bankDescription}</TableCell>
                                <TableCell>
                                    {bank.accountHolder && bank.accountHolder.length > 20 ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {bank.accountHolder.substring(0, 20)}...
                                            <Popover withArrow trapFocus>
                                                <PopoverTrigger disableButtonEnhancement>
                                                    <Button icon={<Info24Regular />} appearance="transparent" size="small" />
                                                </PopoverTrigger>
                                                <PopoverSurface>
                                                    <div>{bank.accountHolder}</div>
                                                </PopoverSurface>
                                            </Popover>
                                        </div>
                                    ) : (
                                        bank.accountHolder
                                    )}
                                </TableCell>
                                <TableCell>{bank.bankName}</TableCell>
                                <TableCell>{bank.iban}</TableCell>
                                <TableCell>{bank.bic}</TableCell>
                                <TableCell>{bank.directDebit && <CheckmarkCircle24Regular />}</TableCell>
                                <TableCell>
                                    <Edit24Regular style={{ cursor: "pointer", marginRight: 8 }} onClick={(event) => { event.stopPropagation(); setSelectedBank(bank); }} />
                                    <Delete24Regular style={{ cursor: "pointer" }} onClick={(event) => { event.stopPropagation(); handleDeleteBank(bank.id); }} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>

            {selectedBank && (
                <div style={{ marginTop: 20, padding: 20, border: "1px solid #ccc", borderRadius: 4, position: "relative" }}>
                    <Dismiss24Regular style={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }} onClick={() => setSelectedBank(null)} />
                    <h4>Bankverbindung bearbeiten</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Input value={bankDesc} onChange={(_, data) => setBankDesc(data.value)} placeholder="Bezeichnung" />
                        <Input value={bankAccountHolder} onChange={(_, data) => setBankAccountHolder(data.value)} placeholder="Kontoinhaber" />
                        <Input value={bankName} onChange={(_, data) => setBankName(data.value)} placeholder="Bank" />
                        <Input value={bankIban} onChange={(_, data) => setBankIban(data.value)} placeholder="IBAN" />
                        <Input value={bankBic} onChange={(_, data) => setBankBic(data.value)} placeholder="BIC" />
                    </div>
                    <div style={{ display: "flex", gap: "20px", marginTop: 10 }}>
                        <Checkbox checked={bankDefault} onChange={(_, data) => setBankDefault(data.checked === true)} label="Als Standard festlegen" />
                        <Checkbox checked={bankDirectDebit} onChange={(_, data) => setBankDirectDebit(data.checked === true)} label="Bankeinzug" />
                    </div>
                    <Button appearance="primary" onClick={handleSaveBank} style={{ marginTop: 10 }}>Speichern</Button>
                </div>
            )}
        </div>
    );
};
